/**
 * Flywheel Configuration Schema
 * Validation schema for the flywheel configuration form
 */

import { z } from 'zod';

/**
 * Zod schema for flywheel configuration validation
 * Uses conditional validation - only validates required fields when flywheel is enabled
 */
export const flywheelConfigSchema = z
  .object({
    flywheelEnabled: z.boolean(),
    transferLines: z.array(z.string()),
    startHour: z.string(),
    endHour: z.string(),
    selectedDays: z.array(z.string()),
    timezone: z.string(),
    countryCode: z.string(),
  })
  .superRefine((data, ctx) => {
    // Only validate these fields when flywheel is enabled
    if (data.flywheelEnabled) {
      if (data.transferLines.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one transfer line is required',
          path: ['transferLines'],
        });
      }
      if (data.selectedDays.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one day must be selected',
          path: ['selectedDays'],
        });
      }
      if (!data.timezone) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Timezone is required',
          path: ['timezone'],
        });
      }
    }
  });

/**
 * Type inference from schema for form data
 */
export type FlywheelConfigFormData = z.infer<typeof flywheelConfigSchema>;

/**
 * Default values for flywheel config form
 * Note: timezone starts empty to force user selection
 */
export const createDefaultFlywheelConfig = (timezone?: string): FlywheelConfigFormData => ({
  flywheelEnabled: false,
  transferLines: [],
  startHour: '9',
  endHour: '17',
  selectedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  timezone: timezone || '', // Start empty to force user selection
  countryCode: 'US',
});
